import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Mail, Loader2, ShieldCheck, DollarSign, Rocket, Users, ExternalLink, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import AffiliateTerms from "./AffiliateTerms";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AffiliateJoinDialog = ({ open, onOpenChange }: Props) => {
  const { toast } = useToast();
  const [showActivate, setShowActivate] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    name: "",
    email: "",
    phone: "",
    socialInstagram: "",
    socialTwitter: "",
    socialTiktok: "",
    primaryPlatform: "",
  });

  const handleActivate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Please sign in with your account first, then activate the programme.");
      const { data: res, error } = await supabase.functions.invoke("activate-affiliate", { body: data });
      if (error) throw error;
      if (res?.success) {
        toast({ title: "Verification email sent!", description: "Check your inbox to activate your affiliate account." });
        setShowActivate(false);
        onOpenChange(false);
        setData({ name: "", email: "", phone: "", socialInstagram: "", socialTwitter: "", socialTiktok: "", primaryPlatform: "" });
      } else {
        throw new Error(res?.error || "Failed to activate");
      }
    } catch (err: any) {
      toast({ variant: "destructive", title: "Activation failed", description: err.message || "Please try again later" });
    } finally {
      setLoading(false);
    }
  };

  const perks = [
    { icon: DollarSign, title: "30% Commission", desc: "Earn on every referred creator or venue's first month." },
    { icon: Rocket, title: "Fast Payouts", desc: "Get paid quickly through our streamlined payout system." },
    { icon: Users, title: "Referral Discount", desc: "Your referrals get 30% off their first month." },
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl bg-black text-white border-white/10 p-0 overflow-hidden">
          <div className="relative p-8 bg-gradient-to-br from-purple-600/20 via-black to-pink-600/10 border-b border-white/10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs font-semibold mb-4">
              <Sparkles className="w-3.5 h-3.5" /> AFFILIATE PROGRAM
            </div>
            <DialogHeader className="text-left space-y-2">
              <DialogTitle className="text-3xl font-bold tracking-tight">
                Earn with Metro City Groove
              </DialogTitle>
              <DialogDescription className="text-gray-300 text-base">
                Refer creators and venues, earn commissions, and grow with the culture.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="p-8 space-y-6">
            <div className="grid sm:grid-cols-3 gap-3">
              {perks.map((p) => (
                <div key={p.title} className="p-4 rounded-xl bg-white/5 border border-white/10">
                  <p.icon className="w-5 h-5 text-purple-400 mb-2" />
                  <h4 className="font-semibold text-sm">{p.title}</h4>
                  <p className="text-xs text-gray-400 mt-1">{p.desc}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                onClick={() => setShowActivate(true)}
                className="flex-1 h-12 bg-white text-black hover:bg-gray-200 font-semibold"
              >
                <Mail className="w-4 h-4 mr-2" /> Activate Program
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowTerms(true)}
                className="flex-1 h-12 border-white/30 bg-transparent text-white hover:bg-white/10"
              >
                View Terms <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </div>
            <p className="text-xs text-center text-gray-500">
              Exclusively available for creators and venues. Email verification required.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Activation form */}
      <Dialog open={showActivate} onOpenChange={setShowActivate}>
        <DialogContent className="sm:max-w-md bg-white text-gray-900 border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Mail className="w-5 h-5" /> Activate Affiliate Program
            </DialogTitle>
            <DialogDescription className="text-gray-500">
              A verification email will be sent to activate your account
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleActivate} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="aff-name">Full Name *</Label>
              <Input id="aff-name" value={data.name} onChange={(e) => setData((p) => ({ ...p, name: e.target.value }))} placeholder="John Doe" required maxLength={100} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="aff-email">Email Address *</Label>
              <Input id="aff-email" type="email" value={data.email} onChange={(e) => setData((p) => ({ ...p, email: e.target.value }))} placeholder="john@example.com" required maxLength={255} />
              <p className="text-xs text-gray-500">A verification link will be sent to this email</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="aff-phone">Phone Number</Label>
              <Input id="aff-phone" value={data.phone} onChange={(e) => setData((p) => ({ ...p, phone: e.target.value }))} placeholder="+27 12 345 6789" />
            </div>

            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-blue-600 mt-0.5" />
                <div className="text-xs text-blue-800">
                  <strong>Secure Activation:</strong> Your affiliate account activates only after email verification. Your referrals get 30% off their first month!
                </div>
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-black hover:bg-gray-900 text-white font-semibold">
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending Verification...</>) : (<><Mail className="w-4 h-4 mr-2" /> Send Verification Email</>)}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <AffiliateTerms open={showTerms} onOpenChange={setShowTerms} />
    </>
  );
};

export default AffiliateJoinDialog;
